'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useCallback, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Immutable from 'immutable';
import { parse as parseParams } from 'hub-http/helpers/params';
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import UIFieldset from 'UIComponents/form/UIFieldset';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import MediaQuery from 'ecosystem-marketplace-core/components/layout/MediaQuery';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { usePrevious } from 'ecosystem-marketplace-core/hooks/usePrevious';
import { useScrollToTop } from 'ecosystem-marketplace-core/hooks/useScrollToTop';
import { isUninitialized } from 'ecosystem-marketplace-core/utils/requestState';
import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
import MainSection from 'ecosystem-marketplace-core/components/layout/MainSection';
import PlanCompatibilityFilter from 'marketplace-ui-common/filters/components/PlanCompatibilityFilter';
import * as GroupTagsActions from '../actions/GroupTagActions';
import Header from '../components/storefront/Header';
import SidebarSection from '../components/storefront/sidebar/SidebarSection';
import AppLanguagesSelect from '../components/storefront/sidebar/filters/AppLanguagesSelect';
import CertifiedToggle from '../components/storefront/sidebar/filters/CertifiedToggle';
import DataSyncDirectionFilter from '../components/storefront/sidebar/filters/DataSyncDirectionFilter';
import HubSpotFeaturesFilter from '../components/storefront/sidebar/filters/HubSpotFeaturesFilter';
import IntegrationsFilter from '../components/storefront/sidebar/filters/IntegrationsFilter';
import PriceFilter from '../components/storefront/sidebar/filters/PriceFilter';
import SupportedLanguagesSelect from '../components/storefront/sidebar/filters/SupportedLanguagesSelect';
import SupportSourceFilter from '../components/storefront/sidebar/filters/SupportSourceFilter';
import CategoryFilterNav from '../components/storefront/sidebar/nav/CategoryFilterNav';
import CollectionsNav from '../components/storefront/sidebar/nav/CollectionsNav';
import HSPageViewTracker from '../components/tracking/HSPageViewTracker';
import MobileNavigator from '../components/storefront/results/mobile/MobileNavigator';
import * as FilterKey from '../constants/search/FilterKey';
import StorefrontFiltersContext from '../context/StorefrontFiltersContext';
import StorefrontUsageTrackerContext from '../context/StorefrontUsageTrackerContext';
import StorefrontFilters from '../records/StorefrontFilters';
import { getAppIntegrations } from '../selectors/appIntegrations';
import { getIsCustomer } from '../selectors/auth';
import { makeGetCollectionListingBySlug } from '../selectors/collections';
import { getAppIntegrationsRequestState } from '../selectors/requestStates';
import { debug } from '../utils/env';
import { filterFeaturesByCategory } from '../utils/groupTags';
import { getListingDetailUrl, stripPathPagination, getHasPathPagination, getAllCategoriesUrl, getAllCollectionsUrl } from '../utils/urls';
import { getUsageTracker, getBeforeUnloadUsageTracker } from '../utils/usageTracker';
import useWindowWidth from '../utils/hooks/useWindowWidth';
import { useParsedPathParams } from '../utils/hooks/useParsedPathParams';
const ALL_COLLECTIONS_REGEX = new RegExp(`^${getAllCollectionsUrl()}$`);
const ALL_CATEGORIES_REGEX = new RegExp(`^${getAllCategoriesUrl()}`);

const Storefront = ({
  children,
  isAdminPreview = false
}) => {
  const history = useHistory();
  const {
    search,
    pathname
  } = useLocation();
  const params = useParams();
  const language = getLang();
  useScrollToTop(pathname);
  const appIntegrations = useSelector(getAppIntegrations);
  const appIntegrationsRequestState = useSelector(getAppIntegrationsRequestState);
  const isCustomer = useSelector(getIsCustomer);
  const getCollectionListingBySlug = useMemo(() => makeGetCollectionListingBySlug(), []);
  const collectionListing = useSelector(state => params.collection ? getCollectionListingBySlug(state, {
    slug: params.collection
  }) : null);
  const dispatch = useDispatch();
  const fetchAppIntegrations = useCallback(() => dispatch(GroupTagsActions.fetchAppIntegrations()), [dispatch]);
  const {
    category: activeCategory,
    superCategory: activeSuperCategory,
    page: activePage = 1
  } = useParsedPathParams(params);
  const isCollection = !!collectionListing;
  const isAllCollections = ALL_COLLECTIONS_REGEX.test(pathname);
  const isAllCategories = ALL_CATEGORIES_REGEX.test(pathname);
  const queryString = search.slice(1);
  const {
    source
  } = parseParams(queryString);
  const prevActiveCategory = usePrevious(activeCategory);
  const prevActiveSuperCategory = usePrevious(activeSuperCategory);
  const filters = useMemo(() => {
    return new StorefrontFilters().withMutations(mutableFilters => {
      mutableFilters.set(FilterKey.CATEGORY, activeCategory);
      mutableFilters.set(FilterKey.SUPER_CATEGORY, activeSuperCategory);
      mutableFilters.fromQueryString(queryString);
    });
  }, [activeCategory, activeSuperCategory, queryString]);
  const {
    [FilterKey.CATEGORY_FEATURES]: categoryFeaturesValue,
    [FilterKey.PRICING]: planTypeValue,
    [FilterKey.PLAN_COMPATIBLE]: planCompatibleValue,
    [FilterKey.TIERS]: tiersValue,
    [FilterKey.HUBSPOT_FEATURES]: hubspotFeaturesValue,
    [FilterKey.INTEGRATIONS]: integrationsValue,
    [FilterKey.CERTIFIED]: certifiedValue,
    [FilterKey.SYNC]: syncValue,
    [FilterKey.APP_LANGUAGES]: appLanguagesValue,
    [FilterKey.SUPPORT_LANGUAGES]: supportedLanguagesValue,
    [FilterKey.SUPPORT_SOURCE]: supportSourceValue,
    [FilterKey.SEARCH]: searchValue
  } = filters;
  const usageTracker = getUsageTracker();
  const beforeUnloadUsageTracker = getBeforeUnloadUsageTracker();
  const usageEventProperties = useMemo(() => {
    const properties = Object.assign({
      screen: 'storefront',
      preferredLanguage: language
    }, filters.toEventProperties());

    if (isAllCollections) {
      properties.category = 'all collections';
    } else if (isAllCategories) {
      properties.category = 'all categories';
    } else if (collectionListing) {
      const {
        id: collectionListingId,
        collectionId
      } = collectionListing;
      properties.category = `collection::${collectionId}_${collectionListingId}`;
    }

    return properties;
  }, [collectionListing, filters, isAllCategories, isAllCollections, language]);
  const trackInteraction = useCallback((action, properties = {}, name = 'storefront-interaction', _usageTracker = usageTracker) => {
    _usageTracker.track(name, Object.assign({}, usageEventProperties, {}, properties, {
      action
    }));
  }, [usageEventProperties, usageTracker]);
  const trackBeforeUnloadInteraction = useCallback((action, properties, name) => {
    trackInteraction(action, properties, name, beforeUnloadUsageTracker);
  }, [beforeUnloadUsageTracker, trackInteraction]);
  const trackFilterChange = useCallback((filter, _value) => {
    let value = _value;
    let eventName = 'storefront-change-filter';

    if (Immutable.Set.isSet(value)) {
      value = value.toJS().sort();
      eventName += '-set';
    } else if (typeof value === 'boolean') {
      eventName += '-boolean';
    } else if (value == null) {
      value = '';
    }

    trackInteraction('change filter', {
      filter,
      value
    }, eventName);
  }, [trackInteraction]);
  const trackFilterNavItemClick = useCallback((filter, value) => {
    trackInteraction('click filter nav item', {
      filter,
      value
    }, 'storefront-click-filter-nav-item');
  }, [trackInteraction]);
  const trackResultClick = useCallback((result, itemIndex, properties = {}) => {
    trackInteraction('click result item', Object.assign({
      value: result.slug,
      itemIndex
    }, properties), 'storefront-click-result-item');
  }, [trackInteraction]);
  const trackViewEvent = useCallback((eventName, properties = {}) => {
    usageTracker.track(eventName, Object.assign({}, usageEventProperties, {}, properties));
  }, [usageEventProperties, usageTracker]);
  const trackPageView = useCallback(properties => trackViewEvent('storefront-pageview', Object.assign({}, properties, {
    source
  })), [source, trackViewEvent]);
  const trackFilterView = useCallback(properties => trackViewEvent('storefront-filter', properties), [trackViewEvent]);
  const pushFilters = useCallback((updatedFilters, force) => {
    if (!filters.equals(updatedFilters) || force) {
      history.push(updatedFilters.toLocation());
    }
  }, [filters, history]);
  const replaceFilters = useCallback(updatedFilters => {
    if (!filters.equals(updatedFilters)) {
      history.replace(updatedFilters.toLocation());
    }
  }, [filters, history]);
  const handleFilterChange = useCallback((key, value) => {
    const updatedFilters = filters.set(key, value);
    pushFilters(updatedFilters);
    trackFilterChange(key, value);
  }, [filters, pushFilters, trackFilterChange]);
  const updateChangedFilters = useCallback(newFilters => {
    const changedFilters = filters.mergeWith((oldVal, newVal, key) => {
      if (oldVal !== newVal) {
        trackFilterChange(key, newVal);
        return newVal;
      }

      return oldVal;
    }, newFilters);
    pushFilters(changedFilters);
  }, [filters, pushFilters, trackFilterChange]);
  const handleClearFilters = useCallback(() => {
    pushFilters(filters.clear());
  }, [filters, pushFilters]);
  const handleCategoryChange = useCallback((superCategory, category) => {
    const updatedFilters = filters.withMutations(mutableFilters => {
      mutableFilters.set(FilterKey.SUPER_CATEGORY, superCategory).set(FilterKey.CATEGORY, category).delete(FilterKey.SEARCH);

      if (category !== activeCategory) {
        mutableFilters.delete(FilterKey.CATEGORY_FEATURES);
      }
    });
    pushFilters(updatedFilters, true);
  }, [activeCategory, filters, pushFilters]);
  const handleSearchAppSelect = useCallback((slug, category, superCategory) => {
    history.push(getListingDetailUrl(slug, category, superCategory));
  }, [history]);
  const handleSearchFeatureSelect = useCallback((superCategory, category, feature) => {
    const updatedFilters = filters.withMutations(mutableFilters => {
      mutableFilters.set(FilterKey.SUPER_CATEGORY, superCategory).set(FilterKey.CATEGORY, category).set(FilterKey.CATEGORY_FEATURES, category === activeCategory ? categoryFeaturesValue.add(feature) : categoryFeaturesValue.clear().add(feature));
    });
    pushFilters(updatedFilters);
  }, [activeCategory, categoryFeaturesValue, filters, pushFilters]);
  const handleSearchFilterSelect = useCallback((filterKey, filterValue) => {
    const updatedFilters = filters.set(filterKey, filters.get(filterKey).add(filterValue));
    pushFilters(updatedFilters);
  }, [filters, pushFilters]);
  const handleSearchSubmit = useCallback(searchFilters => {
    history.push(searchFilters.toLocation());
  }, [history]);
  const handleSearchClear = useCallback(() => {
    const updatedFilters = filters.delete(FilterKey.SEARCH);
    pushFilters(updatedFilters);
  }, [filters, pushFilters]);
  const openFilters = useWindowWidth({
    sm: false,
    md: true
  });
  const renderedCollectionsNavSection = useMemo(() => /*#__PURE__*/_jsx(SidebarSection, {
    defaultOpen: openFilters,
    title: unescapedText('appMarketplace.storefront.filters.collection.titleText'),
    children: /*#__PURE__*/_jsx(CollectionsNav, {
      isAdminPreview: isAdminPreview,
      trackFilterNavItemClick: trackFilterNavItemClick
    })
  }), [openFilters, isAdminPreview, trackFilterNavItemClick]);
  const renderedCategoryFilterNavSection = useMemo(() => /*#__PURE__*/_jsx(SidebarSection, {
    defaultOpen: openFilters,
    title: unescapedText('appMarketplace.storefront.filters.category.titleText'),
    children: /*#__PURE__*/_jsx(CategoryFilterNav, {
      activeCategory: activeCategory,
      activeSuperCategory: activeSuperCategory,
      filters: filters,
      isAdminPreview: isAdminPreview,
      isAllCategories: isAllCategories,
      onCategoryChange: handleCategoryChange,
      onFeaturesChange: values => handleFilterChange(FilterKey.CATEGORY_FEATURES, values),
      selectedFeatures: categoryFeaturesValue,
      trackFilterNavItemClick: trackFilterNavItemClick
    })
  }), [activeCategory, activeSuperCategory, categoryFeaturesValue, filters, handleCategoryChange, handleFilterChange, isAdminPreview, isAllCategories, trackFilterNavItemClick, openFilters]);
  const renderedPriceToggleGroupSection = useMemo(() => /*#__PURE__*/_jsx(SidebarSection, {
    defaultOpen: openFilters,
    title: unescapedText('appMarketplace.storefront.filters.planType.titleText'),
    tooltip: unescapedText('appMarketplace.storefront.filters.planType.sectionTooltip'),
    children: /*#__PURE__*/_jsx(PriceFilter, {
      onChange: event => {
        handleFilterChange(FilterKey.PRICING, event.target.value);
      },
      value: planTypeValue
    })
  }), [handleFilterChange, planTypeValue, openFilters]);
  const renderedCompatibilityToggleGroupSection = useMemo(() => /*#__PURE__*/_jsx(SidebarSection, {
    defaultOpen: openFilters,
    title: unescapedText('appMarketplace.storefront.filters.compatibility.titleText'),
    tooltip: unescapedText('appMarketplace.storefront.filters.compatibility.sectionTooltip'),
    children: /*#__PURE__*/_jsx(PlanCompatibilityFilter, {
      appContext: isCustomer ? 'app' : 'public',
      ariaLabel: unescapedText('appMarketplace.storefront.filters.compatibility.titleText'),
      onChange: event => {
        const {
          portalCompatible,
          tiers
        } = event.target.value;
        handleFilterChange(FilterKey.TIERS, Immutable.Set(tiers));
        handleFilterChange(FilterKey.PLAN_COMPATIBLE, portalCompatible);
      },
      value: {
        portalCompatible: planCompatibleValue,
        tiers: tiersValue.toArray()
      }
    })
  }), [handleFilterChange, isCustomer, planCompatibleValue, tiersValue, openFilters]);
  const renderedHubSpotFeaturesFilterSection = useMemo(() => /*#__PURE__*/_jsx(SidebarSection, {
    defaultOpen: !hubspotFeaturesValue.isEmpty(),
    title: unescapedText('appMarketplace.storefront.filters.hubspotFeatures.titleText'),
    tooltip: unescapedText('appMarketplace.storefront.filters.hubspotFeatures.sectionTooltip'),
    children: /*#__PURE__*/_jsx(HubSpotFeaturesFilter, {
      onChange: event => handleFilterChange(FilterKey.HUBSPOT_FEATURES, Immutable.Set(event.target.value)),
      trackInteraction: trackInteraction,
      value: hubspotFeaturesValue.toArray()
    })
  }), [handleFilterChange, hubspotFeaturesValue, trackInteraction]);
  const renderedIntegrationsFilterSection = useMemo(() => /*#__PURE__*/_jsx(SidebarSection, {
    defaultOpen: !integrationsValue.isEmpty(),
    title: unescapedText('appMarketplace.storefront.filters.integrations.titleText'),
    tooltip: unescapedText('appMarketplace.storefront.filters.integrations.sectionTooltip'),
    children: /*#__PURE__*/_jsx(IntegrationsFilter, {
      appIntegrations: appIntegrations.toArray(),
      onChange: event => handleFilterChange(FilterKey.INTEGRATIONS, event.target.value),
      trackInteraction: trackInteraction,
      value: integrationsValue.toArray()
    })
  }), [appIntegrations, handleFilterChange, integrationsValue, trackInteraction]);
  const renderedCertifiedFilterSection = useMemo(() => /*#__PURE__*/_jsx(SidebarSection, {
    defaultOpen: !!certifiedValue,
    title: unescapedText('appMarketplace.storefront.filters.certified.titleText'),
    tooltip: unescapedText('appMarketplace.storefront.filters.certified.sectionTooltip'),
    children: /*#__PURE__*/_jsx(CertifiedToggle, {
      onChange: value => handleFilterChange(FilterKey.CERTIFIED, value),
      value: certifiedValue
    })
  }), [handleFilterChange, certifiedValue]);
  const renderedDataSyncDirectionFilterSection = useMemo(() => /*#__PURE__*/_jsx(SidebarSection, {
    defaultOpen: !!syncValue,
    title: unescapedText('appMarketplace.storefront.filters.sync.titleText'),
    tooltip: unescapedText('appMarketplace.storefront.filters.sync.sectionTooltip'),
    children: /*#__PURE__*/_jsx(DataSyncDirectionFilter, {
      onChange: event => handleFilterChange(FilterKey.SYNC, event.target.value),
      value: syncValue
    })
  }), [handleFilterChange, syncValue]);
  const renderedAppLanguagesFilterSection = useMemo(() => /*#__PURE__*/_jsx(SidebarSection, {
    defaultOpen: !appLanguagesValue.isEmpty(),
    title: unescapedText('appMarketplace.storefront.filters.appLanguages.titleText'),
    tooltip: unescapedText('appMarketplace.storefront.filters.appLanguages.sectionTooltip'),
    children: /*#__PURE__*/_jsx(AppLanguagesSelect, {
      onChange: value => handleFilterChange(FilterKey.APP_LANGUAGES, value),
      value: appLanguagesValue
    })
  }), [handleFilterChange, appLanguagesValue]);
  const renderedSupportedLanguagesFilterSection = useMemo(() => /*#__PURE__*/_jsx(SidebarSection, {
    defaultOpen: !supportedLanguagesValue.isEmpty(),
    title: unescapedText('appMarketplace.storefront.filters.supportedLanguages.titleText'),
    tooltip: unescapedText('appMarketplace.storefront.filters.supportedLanguages.sectionTooltip'),
    children: /*#__PURE__*/_jsx(SupportedLanguagesSelect, {
      onChange: value => handleFilterChange(FilterKey.SUPPORT_LANGUAGES, value),
      value: supportedLanguagesValue
    })
  }), [handleFilterChange, supportedLanguagesValue]);
  const renderedSupportSourceFilterSection = useMemo(() => /*#__PURE__*/_jsx(SidebarSection, {
    defaultOpen: !!supportSourceValue,
    title: unescapedText('appMarketplace.storefront.filters.supportSource.titleText'),
    tooltip: unescapedText('appMarketplace.storefront.filters.supportSource.sectionTooltip'),
    children: /*#__PURE__*/_jsx(SupportSourceFilter, {
      onChange: event => {
        handleFilterChange(FilterKey.SUPPORT_SOURCE, event.target.value);
      },
      value: supportSourceValue
    })
  }), [handleFilterChange, supportSourceValue]);
  useEffect(() => {
    if (isUninitialized(appIntegrationsRequestState) && !isAdminPreview) {
      fetchAppIntegrations();
    }
  }, [appIntegrationsRequestState, fetchAppIntegrations, isAdminPreview]);
  useEffect(() => {
    if (getHasPathPagination(pathname) && activePage === 1) {
      history.replace({
        pathname: stripPathPagination(pathname),
        search
      });
    }
  }, [activePage, history, pathname, search]);
  useEffect(() => {
    if (activeCategory !== prevActiveCategory || activeSuperCategory !== prevActiveSuperCategory) {
      const updatedFilters = filters.withMutations(mutableFilters => {
        mutableFilters.set(FilterKey.CATEGORY_FEATURES, activeCategory ? filterFeaturesByCategory(mutableFilters.get(FilterKey.CATEGORY_FEATURES), activeCategory) : categoryFeaturesValue.clear());
      });
      replaceFilters(updatedFilters);
    }
  }, [activeCategory, activeSuperCategory, filters, prevActiveCategory, prevActiveSuperCategory, replaceFilters, categoryFeaturesValue]);
  useEffect(() => {
    if (debug('storefront-filters')) {
      console.groupCollapsed('storefront filters');
      console.log('filters:\n', filters.toJS());
      console.log('query:\n', filters.toQuery());
      console.log('search query:\n', filters.toSearchQuery());
      console.log('query string:\n', filters.toQueryString());
      console.groupEnd();
    }
  }, [filters]);
  const disableFilters = isAllCollections || isCollection || isAdminPreview;
  const {
    paddingClassNames
  } = useResponsiveProps({
    paddingClassNames: {
      xs: 'p-bottom-5 p-top-0',
      md: "p-y-10 p-x-12"
    }
  });
  return /*#__PURE__*/_jsx(StorefrontFiltersContext.Provider, {
    value: {
      filters,
      isAllCategories,
      clearFilters: handleClearFilters,
      pushFilters,
      updateFilter: handleFilterChange,
      updateAllFilters: updateChangedFilters
    },
    children: /*#__PURE__*/_jsxs(StorefrontUsageTrackerContext.Provider, {
      value: {
        trackInteraction,
        trackBeforeUnloadInteraction,
        trackPageView,
        trackFilterView,
        trackResultClick
      },
      children: [/*#__PURE__*/_jsx(HSPageViewTracker, {
        pathname: pathname
      }), /*#__PURE__*/_jsx(Header, {
        activeCategory: activeCategory,
        activeSuperCategory: activeSuperCategory,
        appIntegrations: appIntegrations,
        headingLevel: isAllCollections ? 'h1' : 'h2',
        isAdminPreview: isAdminPreview,
        isCustomer: isCustomer,
        onSearchAppSelect: handleSearchAppSelect,
        onSearchCategorySelect: handleCategoryChange,
        onSearchClear: handleSearchClear,
        onSearchFeatureSelect: handleSearchFeatureSelect,
        onSearchFilterSelect: handleSearchFilterSelect,
        onSearchSubmit: handleSearchSubmit,
        searchValue: searchValue,
        trackBeforeUnloadInteraction: trackBeforeUnloadInteraction,
        trackInteraction: trackInteraction
      }), /*#__PURE__*/_jsx(MainSection, {
        "data-test-id": "storefront",
        className: paddingClassNames,
        children: /*#__PURE__*/_jsxs(MainSectionInner, {
          children: [/*#__PURE__*/_jsx(MediaQuery, {
            maxScreenSize: ScreenSizes.MD,
            children: /*#__PURE__*/_jsx(MobileNavigator, {
              className: "m-bottom-5",
              filters: filters,
              isAllCollections: isAllCollections,
              isAllCategories: isAllCategories,
              onCategoryChange: handleCategoryChange,
              url: pathname,
              trackFilterNavItemClick: trackFilterNavItemClick
            })
          }), /*#__PURE__*/_jsxs(UIGrid, {
            children: [/*#__PURE__*/_jsx(MediaQuery, {
              minScreenSize: ScreenSizes.MD,
              children: /*#__PURE__*/_jsx(UIGridItem, {
                size: {
                  xs: 12,
                  md: 3
                },
                children: /*#__PURE__*/_jsx("aside", {
                  children: /*#__PURE__*/_jsxs(UIFieldset, {
                    _size: "small",
                    children: [renderedCollectionsNavSection, renderedCategoryFilterNavSection, !disableFilters && /*#__PURE__*/_jsxs(Fragment, {
                      children: [renderedPriceToggleGroupSection, renderedCompatibilityToggleGroupSection, renderedHubSpotFeaturesFilterSection, renderedIntegrationsFilterSection, renderedCertifiedFilterSection, renderedDataSyncDirectionFilterSection, renderedAppLanguagesFilterSection, renderedSupportedLanguagesFilterSection, renderedSupportSourceFilterSection]
                    })]
                  })
                })
              })
            }), /*#__PURE__*/_jsx(UIGridItem, {
              size: {
                xs: 12,
                md: 9
              },
              children: /*#__PURE__*/_jsx("main", {
                children: children
              })
            })]
          })]
        })
      })]
    })
  });
};

Storefront.propTypes = {
  children: PropTypes.node,
  isAdminPreview: PropTypes.bool
};
export default Storefront;